import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { DataAccessFairsFacade } from '@planone/bau365/data-access-fair-data';
import { ContactPerson, Contributor, Event, Exhibitor, Post, Product } from '@planone/shared/util-interfaces';
import { BreadcrumbData } from '@planone/shared/util-structured-data';

import { BreadcrumbBuilder } from './breadcrumb.builder';
import { BusinessCard, Fair } from './breadcrumb.interfaces';

@Injectable()
export class BreadcrumbHelper {
  private bauInsights = '';

  constructor(@Inject(DOCUMENT) private document: Document, private fairsFacade: DataAccessFairsFacade) {
    this.init();
  }

  private init(): void {
    this.fairsFacade.fairsConfig$.subscribe((fairsConfig) => (this.bauInsights = fairsConfig.bauInsights));
  }

  prepareForExhibitor(fair: Fair, exhibitor: Exhibitor): BreadcrumbData {
    return BreadcrumbBuilder.getInstance(this.bauInsights).forExhibitor(fair, exhibitor).build(this.document);
  }

  prepareForPost(fair: Fair, exhibitor: Exhibitor, post: Post): BreadcrumbData {
    return BreadcrumbBuilder.getInstance(this.bauInsights)
      .forExhibitor(fair, exhibitor)
      .withPostName(fair, post)
      .build(this.document);
  }

  prepareForProduct(fair: Fair, exhibitor: Exhibitor, product: Product): BreadcrumbData {
    return BreadcrumbBuilder.getInstance(this.bauInsights)
      .forExhibitor(fair, exhibitor)
      .withProductName(fair, product)
      .build(this.document);
  }

  prepareForEvent(fair: Fair, exhibitor: Exhibitor, event: Event): BreadcrumbData {
    return BreadcrumbBuilder.getInstance(this.bauInsights)
      .forExhibitor(fair, exhibitor)
      .withEventName(fair, event)
      .build(this.document);
  }

  prepareForExhibitorDirectory(fair: Fair, letter: string, firstAvailableHall?: string): BreadcrumbData {
    const disabledDirectoryLink = letter === 'A' || letter === firstAvailableHall;
    return BreadcrumbBuilder.getInstance(this.bauInsights)
      .forExhibitorDirectory(fair, disabledDirectoryLink)
      .withExhibitorDirectoryLetter(fair, letter)
      .build(this.document);
  }

  prepareForExhibitorSearch(fair: Fair): BreadcrumbData {
    return BreadcrumbBuilder.getInstance(this.bauInsights)
      .forExhibitorDirectory(fair)
      .withExhibitorDirectorySearch(fair)
      .build(this.document);
  }

  prepareForContactPerson(fair: Fair, exhibitor: Exhibitor, contactPerson: ContactPerson): BreadcrumbData {
    return BreadcrumbBuilder.getInstance(this.bauInsights)
      .forExhibitor(fair, exhibitor)
      .withContactPersonName(fair, exhibitor, contactPerson)
      .build(this.document);
  }

  prepareForEventContributor(fair: Fair, exhibitor: Exhibitor, event: Event, contributor: Contributor): BreadcrumbData {
    return BreadcrumbBuilder.getInstance(this.bauInsights)
      .forExhibitor(fair, exhibitor)
      .withEventName(fair, event)
      .withEventContributorName(fair, event, contributor)
      .build(this.document);
  }

  prepareForSearch(fair: Fair): BreadcrumbData {
    return BreadcrumbBuilder.getInstance(this.bauInsights).forFair(fair, true).withSearch(fair).build(this.document);
  }

  prepareForNewsfeed(): BreadcrumbData {
    return BreadcrumbBuilder.getInstance(this.bauInsights).forRoot(true).withNewsfeed().build(this.document);
  }

  prepareForWatchlist(): BreadcrumbData {
    return BreadcrumbBuilder.getInstance(this.bauInsights).forRoot().withWatchlist().build(this.document);
  }

  prepareForUserProfile(): BreadcrumbData {
    return BreadcrumbBuilder.getInstance(this.bauInsights).forRoot().withUserProfile().build(this.document);
  }

  prepareForMyBusinessCard(): BreadcrumbData {
    return BreadcrumbBuilder.getInstance(this.bauInsights).forRoot().withMyBusinessCard().build(this.document);
  }

  prepareForBusinessCard(businessCard: BusinessCard): BreadcrumbData {
    return BreadcrumbBuilder.getInstance(this.bauInsights)
      .forRoot()
      .withBusinessCard(businessCard, true)
      .withBusinessCardName(businessCard)
      .build(this.document);
  }

  prepareForInvalidBusinessCard(): BreadcrumbData {
    return BreadcrumbBuilder.getInstance(this.bauInsights)
      .forRoot()
      .withBusinessCard({}, true)
      .withInvalidBusinessCard(true)
      .build(this.document);
  }

  prepareForTickets(): BreadcrumbData {
    return BreadcrumbBuilder.getInstance(this.bauInsights).forRoot().withTickets().build(this.document);
  }

  prepareForUserProfileCompletion(): BreadcrumbData {
    return BreadcrumbBuilder.getInstance(this.bauInsights)
      .forRoot()
      .withUserProfile()
      .withUserProfileCompletion()
      .build(this.document);
  }

  prepareForProgram(fair: Fair): BreadcrumbData {
    return BreadcrumbBuilder.getInstance(this.bauInsights).forFair(fair).withProgram(fair).build(this.document);
  }

  prepareForFairsPage(): BreadcrumbData {
    return BreadcrumbBuilder.getInstance(this.bauInsights).forRoot().withAllFairs().build(this.document);
  }

  prepareForQRCodeScanner(): BreadcrumbData {
    return BreadcrumbBuilder.getInstance(this.bauInsights).forRoot().withQRCodeSCanner().build(this.document);
  }
}
